import { useEffect, useRef } from 'react';

interface ContextMenuItem {
  /** The action's name, as its `context-<id>` test id spells it (`new-file`, `rename-folder`). */
  id: string;
  label: string;
  action: () => void;
  danger?: boolean;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    // Delay to prevent immediate close from the same click that opened the menu
    const timeout = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        background: '#252526',
        border: '1px solid #3c3c3c',
        borderRadius: '4px',
        padding: '4px 0',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={`context-menu-item${item.danger ? ' context-menu-danger' : ''}`}
          data-testid={`context-${item.id}`}
          onClick={(e) => {
            e.stopPropagation();
            item.action();
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
}
